import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
} from "chart.js";
import { monthOptions } from "common/utils/utils";
import { Line } from "react-chartjs-2";
import { store } from "app/store";
import { useGetImpactDataPointsByImpactIdQuery } from "features/impacts/impactsSlice";

ChartJS.register(
  ArcElement,

  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
);

const FocusScore = ({
  userid,
  impactid,
  formType,
  targetDirection,
  trackingFrequency,
  fiscalYearStart,
}) => {
  let userId = userid;
  if (!userId) {
    userId = store.getState().auth.userData.id;
  }

  // Chart Model
  let scoreData = {
    labels: [], // will be populated later
    datasets: [
      {
        data: [], // will be populated later
        borderColor: "#1f2b5c",
        backgroundColor: "#9ac8ea",
        fill: true,
      },
    ],
    borderWidth: 1,
  };

  const {
    data: impactHistoryData,
    isError: isImpactHistoryError,
    isSuccess: isImpactHistorySuccess,
  } = useGetImpactDataPointsByImpactIdQuery(impactid, {
    refetchOnMountOrArgChange: true,
  });

  const getScore = (performance_ratio) => {
    let score = 0;
    if (performance_ratio < 0.1) {
      score = 1;
    } else if (performance_ratio >= 0.1 && performance_ratio < 0.2) {
      score = 2;
    } else if (performance_ratio >= 0.2 && performance_ratio < 0.3) {
      score = 3;
    } else if (performance_ratio >= 0.3 && performance_ratio < 0.4) {
      score = 4;
    } else if (performance_ratio >= 0.4 && performance_ratio < 0.5) {
      score = 5;
    } else if (performance_ratio >= 0.5 && performance_ratio < 0.6) {
      score = 6;
    } else if (performance_ratio >= 0.6 && performance_ratio < 0.9) {
      score = 7;
    } else if (performance_ratio >= 0.9 && performance_ratio < 1.1) {
      score = 8;
    } else if (performance_ratio >= 1.1 && performance_ratio < 1.3) {
      score = 9;
    } else if (performance_ratio >= 1.3) {
      score = 10;
    }

    return score;
  };

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const calculateScoreDataPointsForImpact = (data) => {
    if (trackingFrequency === "Monthly") {
      const getLastThreeMonth = (month, year) => {
        const months = [];
        for (let i = 0; i < 3; i++) {
          const prevMonth = (month - i + 12) % 12;
          const prevYear = prevMonth > month ? year - 1 : year;
          months.push(`${monthOptions[prevMonth]} ${prevYear}`);
        }
        return months.reverse();
      };
      const lastThreeMonth = getLastThreeMonth(currentMonth, currentYear);
      const filteredData = lastThreeMonth
        .map((month) => {
          const [monthName, year] = month.split(" ");
          const item = data.find((item) => {
            return item.title === monthName && item.year === parseInt(year);
          });
          return item
            ? {
                month: monthName,
                cumulative_value: item.cumulative_value,
                cumulative_target: item.cumulative_target,
              }
            : null;
        })
        .filter((item) => item !== null);
      filteredData.forEach(function (point) {
        let currentAverage;
        let targetAverage;

        currentAverage = point.cumulative_value;
        targetAverage = point.cumulative_target;
        scoreData.labels.push(point.month);
        if (targetDirection === "Increase") {
          scoreData.datasets[0].data.push(
            getScore(currentAverage / targetAverage),
          );
        }
        if (targetDirection === "Decrease") {
          scoreData.datasets[0].data.push(
            getScore(targetAverage / currentAverage),
          );
        }
      });
    }
    //tracking frequncy as quarterly
    if (trackingFrequency === "Quarterly") {
      const getCurrentQuarter = (currentMonth, fiscalYearStart) => {
        fiscalYearStart = 1;
        const fiscalMonth = (currentMonth - fiscalYearStart + 12) % 12;
        return Math.floor(fiscalMonth / 3) + 1;
      };
      const getLastThreeQuarters = (quarter, year) => {
        const quarters = [];
        for (let i = 0; i < 3; i++) {
          quarters.push({ quarter: `Q${quarter}`, year: year });
          quarter--;
          if (quarter < 1) {
            quarter = 4;
            year--;
          }
        }
        return quarters.reverse();
      };
      const currentQuarter = getCurrentQuarter(currentMonth, fiscalYearStart);
      const lastThreeQuarters = getLastThreeQuarters(
        currentQuarter,
        currentYear,
      );
      const filteredData = lastThreeQuarters.map((point) => {
        let quarter = point.quarter;
        let year = point.year;
        const item = data.find((item) => {
          return item.title === quarter && item.year === year;
        });
        return item
          ? {
              quarter,
              year,
              cumulative_value: item.cumulative_value,
              cumulative_target: item.cumulative_target,
            }
          : { quarter, year, cumulative_value: 0, cumulative_target: 0 };
      });
      filteredData.forEach(function (point) {
        let currentAverage;
        let targetAverage;
        if (point != null) {
          currentAverage = point.cumulative_value;
          targetAverage = point.cumulative_target;
          scoreData.labels.push(`${point.quarter} ${point.year}`);
        } else {
          currentAverage = 0;
          targetAverage = 0;
          scoreData.labels.push(`${point.quarter} ${point.year}`);
        }
        if (targetDirection === "Increase") {
          scoreData.datasets[0].data.push(
            getScore(currentAverage / targetAverage),
          );
        }
        if (targetDirection === "Decrease") {
          scoreData.datasets[0].data.push(
            getScore(targetAverage / currentAverage),
          );
        }
      });
    }
    //tracking frequnecy as yearly
    if (trackingFrequency === "Yearly") {
      const getLastThreeYears = (year) => {
        const years = [];
        for (let i = 0; i < 3; i++) {
          years.push({ year: year });
          year--;
        }
        return years.reverse();
      };

      const lastThreeYears = getLastThreeYears(currentYear);
      const filteredData = lastThreeYears.map((point) => {
        let year = point.year;
        const item = data.find((item) => {
          return Number(item.title) === year;
        });
        return item
          ? {
              year,
              cumulative_value: item.cumulative_value,
              cumulative_target: item.cumulative_target,
            }
          : { year, cumulative_value: 0, cumulative_target: 0 };
      });
      filteredData.forEach(function (point) {
        let currentAverage;
        let targetAverage;
        if (point != null) {
          currentAverage = point.cumulative_value;
          targetAverage = point.cumulative_target;
          scoreData.labels.push(`${point.year}`);
        } else {
          currentAverage = 0;
          targetAverage = 0;
          scoreData.labels.push(`${point.year}`);
        }
        if (targetDirection === "Increase") {
          scoreData.datasets[0].data.push(
            getScore(currentAverage / targetAverage),
          );
        }
        if (targetDirection === "Decrease") {
          scoreData.datasets[0].data.push(
            getScore(targetAverage / currentAverage),
          );
        }
      });
    }
  };

  if (formType === "Impact") {
    if (isImpactHistorySuccess) {
      calculateScoreDataPointsForImpact(impactHistoryData.data);
    }

    if (isImpactHistoryError) {
      calculateScoreDataPointsForImpact([]);
    }
  }

  return (
    <>
      <div className='card equal-height'>
        <div className='card-body'>
          <span className='type-title'>Focus Score</span>

          <div className='row'>
            <Line
              options={{
                scales: {
                  y: {
                    suggestedMin: 1,
                    suggestedMax: 10,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                },
              }}
              data={scoreData}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default FocusScore;
